import {
  Controller,
  get,
  useFormState,
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type RegisterOptions,
} from 'react-hook-form';
import { TextField as MuiTextField, type TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

export type TextFieldProps<TFieldValues extends FieldValues = FieldValues> =
  | ({
      readonly: true;
      defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>;
      disabled?: boolean;
      control?: never;
      rules?: never;
    } & Omit<MuiTextFieldProps, 'name' | 'value' | 'onChange' | 'onBlur'>)
  | ({
      readonly?: false;
      fieldPath: FieldPath<TFieldValues>;
      control?: Control<TFieldValues>;
      rules?: Omit<
        RegisterOptions<TFieldValues, FieldPath<TFieldValues>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
      >;
      defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>;
      disabled?: boolean;
      inputRef?: React.Ref<HTMLInputElement>;
    } & Omit<MuiTextFieldProps, 'name' | 'value' | 'onChange' | 'onBlur'>);

function TextField<TFieldValues extends FieldValues = FieldValues>(
  props: TextFieldProps<TFieldValues>,
) {
  const {
    control,
    rules,
    inputRef,
    disabled,
    defaultValue,
    readonly,
    helperText,
    multiline,
    ...rest
  } = props;
  const { t } = useIntlTranslations();
  if (readonly) {
    return <MuiTextField {...rest} value={defaultValue ?? ''} disabled helperText={helperText} />;
  }

  const { fieldPath, ...restUnControlledInput } = props;
  const { errors } = useFormState({ control, name: fieldPath });
  const error: string = get(errors, fieldPath)?.message;

  return (
    <Controller
      control={control!}
      name={fieldPath}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <MuiTextField
          {...restUnControlledInput}
          rows={multiline ? 4 : 1}
          inputRef={(element: HTMLInputElement) => {
            ref(element);

            if (inputRef) {
              if (typeof inputRef === 'function') {
                inputRef(element);
              } else {
                inputRef.current = element;
              }
            }
          }}
          name={fieldPath}
          disabled={disabled}
          helperText={error ? t(error) : helperText}
          error={!!error}
          value={value || undefined}
          onBlur={e => {
            if (rest.type !== 'number' && typeof value === 'string') {
              onChange(value.trim());
            }
            onBlur();
          }}
          onChange={e => {
            const raw = e.target.value;

            if (rest.type === 'number') {
              if (raw === '') {
                onChange('');
                return;
              }

              const num = Number(raw);
              onChange(!isNaN(num) && num >= 0 ? num : 0);
            } else {
              onChange(raw);
            }
          }}
        />
      )}
    />
  );
}

export default TextField;
